import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useTranslation } from "react-i18next";
import {
  Box,
  Breadcrumbs,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Modal,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Link } from "react-router-dom";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import Div from "@jumbo/shared/Div";
import { useSnackbar } from "notistack";
import { Avatar } from "@mui/material";
import Images from "app/pages/media/images/Images";
import { PROD_URL } from "app/services/config";
import BasicSettingsTab from "./Tabs/BasicSettingsTab";
import VisibilityTab from "./Tabs/VisibilityTab";

import {
  getDSOKCategories,
  getDSOKProducts,
} from "app/services/api/management";
import {
  createDSOKPromotion,
  getDSOKPromotion,
  updateDSOKPromotion,
} from "app/services/api/promotion";

const DsokEditPromotion = () => {
  const { promotionId } = useParams();
  const { t, i18n } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const navigate = useNavigate();
  const [promotion, setPromotion] = useState(null);
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [languageTab, setLanguageTab] = useState(
    defaultLanguage || languages[0]
  );
  const [settingsTab, setSettingsTab] = useState("basic");

  useEffect(() => {
    if (defaultLanguage) setLanguageTab(defaultLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLanguage]);

  useEffect(() => {
    const loadData = async () => {
      // Load categories and products for selects
      try {
        const cats = await getDSOKCategories();
        const categoryArr = Object.keys(cats || {}).map((id) => ({
          ...cats[id],
        }));
        setCategories(categoryArr);
      } catch (e) {
        setCategories([]);
      }
      try {
        const prods = await getDSOKProducts();
        const prodsArr = Array.isArray(prods)
          ? prods
          : Object.keys(prods || {}).map((id) => ({ ...prods[id] }));
        setProducts(prodsArr || []);
      } catch (e) {
        setProducts([]);
      }

      // Load promotion by id or initialize new
      if (promotionId === "new") {
        setPromotion({
          id: null,
          image: "",
          name: {},
          description: {},
          targetType: "category",
          targetId: "",
          isActive: true,
        });
      } else {
        const found = await getDSOKPromotion(promotionId);
        setPromotion(found || null);
      }
    };
    loadData();
  }, [promotionId]);

  const handleSave = async (values) => {
    // Simulate API save
    // eslint-disable-next-line no-console
    if (promotionId === "new") {
      try {
        const result = await createDSOKPromotion(values);
        if (result) {
          enqueueSnackbar(
            t("notifications.savedSuccessfully", {
              defaultValue: "Saved successfully",
            }),
            { variant: "success" }
          );
          navigate("/customer/dsok/promotions");
        } else {
          enqueueSnackbar(result, { variant: "error" });
          navigate("/customer/dsok/promotions");
        }
      } catch (error) {
        enqueueSnackbar(error.response?.data?.message, { variant: "error" });
      }
    } else {
      try {
        const result = await updateDSOKPromotion(promotionId, values);
        if (result) {
          enqueueSnackbar(
            t("notifications.savedSuccessfully", {
              defaultValue: "Saved successfully",
            }),
            { variant: "success" }
          );
          navigate("/customer/dsok/promotions");
        } else {
          enqueueSnackbar(result, { variant: "error" });
          navigate("/customer/dsok/promotions");
        }
      } catch (error) {
        enqueueSnackbar(error.response?.data?.message, { variant: "error" });
      }
    }
  };

  const handleLanguageTabChange = (event, newValue) => setLanguageTab(newValue);
  const handleSettingsTabChange = (event, newValue) => setSettingsTab(newValue);

  const DAYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const buildVisibilityInitial = (visibility) => {
    const normalizeTime = (val) => {
      if (typeof val !== "string") return "";
      const m = val.match(/^(\d{1,2}):(\d{2})/);
      if (!m) return "";
      const hh = String(Math.max(0, Math.min(23, Number(m[1])))).padStart(
        2,
        "0"
      );
      const mm = String(Math.max(0, Math.min(59, Number(m[2])))).padStart(
        2,
        "0"
      );
      return `${hh}:${mm}`;
    };
    const out = {};
    DAYS.forEach((day) => {
      const src = visibility && visibility[day] ? visibility[day] : {};
      const intervals = Array.isArray(src.intervals)
        ? src.intervals
            .map((it) => ({
              from: normalizeTime(it?.from),
              to: normalizeTime(it?.to),
            }))
            .filter((it) => it.from && it.to)
        : [];
      out[day] = {
        enabled: Boolean(src.enabled),
        intervals,
      };
    });
    return out;
  };

  const initialValues = {
    id: promotion?.id || null,
    image: promotion?.image || "",
    name: promotion?.name || {},
    description: promotion?.description || {},
    targetType: promotion?.targetType || "category",
    targetId: promotion?.targetId || "",
    isActive: promotion?.isActive ?? true,
    visibility: buildVisibilityInitial(promotion?.visibility),
  };

  const Schema = Yup.object().shape({
    name: Yup.object().test(
      "at-least-one-language",
      t("validation.atLeastOneLanguage"),
      (value) => {
        if (!value) return false;
        return Object.values(value).some(
          (val) => val && String(val).trim() !== ""
        );
      }
    ),
    description: Yup.object(),
  });

  const getProductDisplayName = (product) => {
    if (!product || typeof product !== "object") {
      return String(product || "");
    }
    const productName = product?.productName;
    const fallbackName = product?.name;
    if (typeof productName === "string") {
      return productName;
    }
    if (
      productName &&
      typeof productName === "object" &&
      !Array.isArray(productName)
    ) {
      return (
        productName?.[defaultLanguage] ||
        productName?.[i18n.language] ||
        Object.values(productName).find((v) => typeof v === "string" && v) ||
        ""
      );
    }
    if (typeof fallbackName === "string") {
      return fallbackName;
    }
    if (
      fallbackName &&
      typeof fallbackName === "object" &&
      !Array.isArray(fallbackName)
    ) {
      return (
        fallbackName?.[defaultLanguage] ||
        fallbackName?.[i18n.language] ||
        Object.values(fallbackName).find((v) => typeof v === "string" && v) ||
        ""
      );
    }
    return "";
  };

  const getCategoryDisplayName = (category) => {
    if (!category || typeof category !== "object") {
      return String(category || "");
    }
    const name = category?.name;
    if (!name) return "";
    if (typeof name === "string") return name;
    if (typeof name === "object") {
      return (
        name?.[defaultLanguage] ||
        name?.[i18n.language] ||
        Object.values(name).find((v) => typeof v === "string" && v) ||
        ""
      );
    }
    return "";
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/promotions">
          {t("sidebar.menuItem.dsokPromotions", { defaultValue: "Promotions" })}
        </Link>
        <Typography color="text.primary">
          {promotion?.name?.[defaultLanguage] ||
            t("pages.dsokPromotions.title")}
        </Typography>
      </Breadcrumbs>
      <Div sx={{ display: "flex", width: "100%", p: 1 }}>
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          {promotion && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={Schema}
              onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                handleSave(values).finally(() => setSubmitting(false));
              }}
            >
              {({
                values,
                setFieldValue,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TabContext value={settingsTab}>
                        <Box
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            mb: 2,
                          }}
                        >
                          <TabList
                            onChange={handleSettingsTabChange}
                            aria-label="settings tabs"
                          >
                            <Tab
                              label={t("pages.dsokPromotions.basicSettings", {
                                defaultValue: "Basic settings",
                              })}
                              value="basic"
                            />
                            <Tab
                              label={t("pages.dsokCategories.visibility", {
                                defaultValue: "Visibility",
                              })}
                              value="visibility"
                            />
                          </TabList>
                        </Box>
                        <TabPanel value="basic" sx={{ p: 0 }}>
                          <BasicSettingsTab
                            t={t}
                            values={values}
                            setFieldValue={setFieldValue}
                            handleBlur={handleBlur}
                            languages={languages}
                            languageTab={languageTab}
                            onLanguageTabChange={handleLanguageTabChange}
                            UploadComponent={UploadComponent}
                            categories={categories}
                            products={products}
                            getCategoryDisplayName={getCategoryDisplayName}
                            getProductDisplayName={getProductDisplayName}
                          />
                        </TabPanel>
                        <TabPanel value="visibility" sx={{ p: 0 }}>
                          <VisibilityTab
                            t={t}
                            DAYS={DAYS}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </TabPanel>
                      </TabContext>
                    </Grid>
                    <Grid item xs={12}>
                      <Div
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "flex-end",
                          pt: 3,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => navigate("/customer/dsok/promotions")}
                        >
                          {t("buttons.cancel")}
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isSubmitting}
                          size="large"
                        >
                          {t("buttons.save")}
                        </Button>
                      </Div>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
        </Box>
      </Div>
    </React.Fragment>
  );
};

const UploadComponent = ({ name, setFieldValue, value }) => {
  const { t } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customerId } = getAuthUser();
  const [openSelector, setOpenSelector] = useState(false);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "75vw",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      <Box
        onClick={() => setOpenSelector(true)}
        sx={{
          width: "100%",
          minHeight: "160px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 1,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        {value ? (
          <Box sx={{ position: "relative", width: "100%" }}>
            <Avatar
              src={typeof value === "string" ? value : ""}
              variant="rounded"
              sx={{
                height: "auto",
                width: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("pages.dsokPromotions.selectImage", {
              defaultValue: "Click to select image",
            })}
          </Typography>
        )}
      </Box>
      <Modal
        open={!!openSelector}
        onClose={() => setOpenSelector(false)}
        aria-labelledby="modal-image-selector-title"
        aria-describedby="modal-image-selector-description"
      >
        <Div sx={modalStyle}>
          <Images
            layout={"row-reverse"}
            selectorMode
            allowed={["jpg", "jpeg", "png", "svg"]}
            onCancel={() => setOpenSelector(false)}
            onSelect={(image) => {
              const imageUrl = `${PROD_URL}/uploads/files/${customerId}/Images/${image.filename}`;
              setFieldValue(name, imageUrl);
              setOpenSelector(false);
            }}
          />
        </Div>
      </Modal>
    </>
  );
};

export default DsokEditPromotion;
